import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, CalendarCheck, Home, QrCode, User, Wallet } from 'lucide-react'
import { BrandMark, ThemeToggle, ByPokerLauncher } from '../../ui/Brand'
import { cx } from '../../ui/format'

/**
 * Frame for the React player pages (/dashboard for players, /wallet, /book-seat,
 * /my-bookings, /player-settings): a simple header, a centred phone-width column
 * and the bottom navigation.
 */
export const PlayerPage = ({ title, subtitle, back, right, children, nav = true, onQr, className }) => {
  const navigate = useNavigate()
  return (
    <div className="min-h-screen bg-cm-bg text-cm-text">
      <header className="sticky top-0 z-30 bg-cm-bg border-b border-cm-border">
        <div className="max-w-xl mx-auto flex items-center gap-3 h-14 px-4">
          {back ? (
            <button
              type="button"
              onClick={() => (typeof back === 'string' ? navigate(back) : navigate(-1))}
              className="w-9 h-9 grid place-items-center rounded-cm bg-cm-surface-2 text-cm-text"
              aria-label="Go back"
            >
              <ArrowLeft size={18} />
            </button>
          ) : (
            <BrandMark size={30} />
          )}
          <div className="flex-1 min-w-0">
            {title && <h1 className="font-display text-[17px] font-extrabold m-0 truncate leading-tight">{title}</h1>}
            {subtitle && <p className="text-xs text-cm-muted m-0 truncate">{subtitle}</p>}
          </div>
          {right}
          <ThemeToggle />
        </div>
      </header>
      <main className={cx('max-w-xl mx-auto px-4 pt-4', nav ? 'pb-28' : 'pb-10', className)}>
        {children}
        <div className="flex justify-center pt-8">
          <ByPokerLauncher height={20} />
        </div>
      </main>
      {nav && <PlayerBottomNav onQr={onQr} />}
    </div>
  )
}

const NAV = [
  { to: '/dashboard', label: 'Home', icon: Home },
  { to: '/wallet', label: 'Wallet', icon: Wallet },
  { to: 'qr', label: 'My QR', icon: QrCode, fab: true },
  { to: '/my-bookings', label: 'Bookings', icon: CalendarCheck },
  { to: '/player-settings', label: 'Profile', icon: User },
]

/** Bottom tab bar. The centre QR button opens the check-in QR (`onQr`) or goes home. */
export const PlayerBottomNav = ({ onQr }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  return (
    <nav className="player-bottom-nav" aria-label="Player navigation">
      <div className="player-bottom-nav-inner">
        {NAV.map((item) => {
          const Icon = item.icon
          if (item.fab) {
            return (
              <button
                key={item.to}
                type="button"
                className="nav-fab"
                onClick={() => (onQr ? onQr() : navigate('/dashboard?qr=1'))}
                aria-label="Show my check-in QR code"
              >
                <Icon size={24} />
              </button>
            )
          }
          const active = pathname === item.to
          return (
            <button
              key={item.to}
              type="button"
              className={cx('nav-item', active && 'active')}
              onClick={() => navigate(item.to)}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={21} />
              <span className="nav-item-label">{item.label}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
